import React, { useMemo, useRef, useState } from "react";
import type { Theme } from "../lib/theme";
import type { BidScope, BidPartner, Bid, LevelingAdjustment } from "../projectData";
import type { BcLevelingSheet, SheetGrid } from "../lib/bcLeveling";
import { parseBcLeveling } from "../lib/bcLeveling";
import { reviewBcImport, buildBcImportPlan } from "../lib/levelingImport";
import { matchSubByCostCode, scopeBudget } from "../lib/scopeBudget";

const FONT = "'Inter','Avenir Next','Century Gothic',sans-serif";
const NEW = "__new__";

/**
 * Read the workbook down to the two things the parser needs.
 *
 * `cellStyles` is not optional: an adjusted cell is marked by its fill, and
 * on the reference export one plug carried a fill and no note at all. Without
 * styles the estimator's plugs become indistinguishable from the bidder's
 * own prices, which is the one distinction the whole import exists to make.
 */
async function readGrid(file: File): Promise<SheetGrid> {
  const XLSX = await import("xlsx");
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellStyles: true, sheetStubs: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  if (!ws || !ws["!ref"]) throw new Error("That workbook has no sheet to read.");
  const R = XLSX.utils.decode_range(ws["!ref"]);
  const values: SheetGrid["values"] = [], fills: SheetGrid["fills"] = [];
  for (let r = R.s.r; r <= R.e.r; r++) {
    const v: (string | number | null)[] = [], f: (string | null)[] = [];
    for (let c = R.s.c; c <= R.e.c; c++) {
      const cell: any = ws[XLSX.utils.encode_cell({ r, c })];
      v.push(cell && cell.v !== undefined ? cell.v : null);
      f.push(cell?.s?.fgColor?.rgb ?? null);
    }
    values.push(v); fills.push(f);
  }
  return { values, fills };
}

export default function BcLevelingImportModal({
  T, fmt, scopes, partners, divisions, itemState, uid, onApply, onClose,
}: {
  T: Theme;
  fmt: (n: number) => string;
  scopes: BidScope[];
  partners: BidPartner[];
  divisions: any[];
  itemState: Record<string, any>;
  uid: () => string;
  onApply: (p: {
    scope: BidScope; isNewScope: boolean; newPartners: BidPartner[];
    bids: Bid[]; adjustments: LevelingAdjustment[];
  }) => void;
  onClose: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [sheet, setSheet] = useState<BcLevelingSheet | null>(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  /** Per bidder: an existing partner id, or NEW. */
  const [choice, setChoice] = useState<string[]>([]);
  const [scopeId, setScopeId] = useState<string>(NEW);

  const review = useMemo(
    () => (sheet ? reviewBcImport(sheet, scopes, partners) : null), [sheet, scopes, partners]);

  const pick = async (file: File) => {
    setBusy(true); setError("");
    try {
      const parsed = parseBcLeveling(await readGrid(file));
      if (!parsed.bidders.length) {
        setError(parsed.warnings[0] || "That does not look like a BuildingConnected leveling export.");
        setSheet(null);
      } else {
        const r = reviewBcImport(parsed, scopes, partners);
        setSheet(parsed); setFileName(file.name);
        setChoice(r.bidders.map(b => b.existing?.id ?? NEW));
        setScopeId(r.matchedScope?.id ?? NEW);
      }
    } catch (e: any) {
      setError(e?.message || "Could not read that file.");
      setSheet(null);
    } finally { setBusy(false); }
  };

  /**
   * The subcategory this package is named after.
   *
   * BC leads a package name with its cost code -- "13-05: Pool , Spa and
   * Interior Diamond Spa" -- and subcategories carry the same codes. Without
   * this the import created a scope covering nothing, so the budget column
   * had no number to compare the bids against.
   */
  const codeMatch = useMemo(
    () => (sheet ? matchSubByCostCode(sheet.packageName, divisions) : null),
    [sheet, divisions]);

  const apply = () => {
    if (!sheet || !review) return;
    const isNewScope = scopeId === NEW;
    const scope: BidScope = isNewScope
      ? {
          id: uid(),
          name: review.packageName || fileName.replace(/\.[^.]+$/, ""),
          divisionIds: codeMatch ? [codeMatch.divId] : [],
          subIds: codeMatch ? [codeMatch.id] : [],
        }
      : scopes.find(s => s.id === scopeId)!;
    const partnerIds = choice.map(c => (c === NEW ? uid() : c));
    const plan = buildBcImportPlan(
      sheet, scope, partnerIds, review.bidders.map(b => b.bidder),
      partners, uid, new Date().toISOString().slice(0, 10));
    onApply({ scope, isNewScope, newPartners: plan.newPartners, bids: plan.bids, adjustments: plan.adjustments });
  };

  const label: React.CSSProperties = {
    fontSize: 10, color: T.textWeak, letterSpacing: ".1em", textTransform: "uppercase",
    fontWeight: 500, padding: "0 8px 7px", textAlign: "left",
  };
  const sel: React.CSSProperties = {
    fontSize: 11, fontFamily: FONT, minHeight: 24, background: T.bgInput,
    border: `1px solid ${T.borderSecondary}`, color: T.textSecondary,
    borderRadius: 3, padding: "4px 7px", outline: "none", width: "100%", boxSizing: "border-box",
  };
  const btn = (primary: boolean, disabled = false): React.CSSProperties => ({
    fontSize: 11, padding: "6px 16px", minHeight: 24, borderRadius: 3, fontFamily: FONT,
    cursor: disabled ? "default" : "pointer", fontWeight: primary ? 600 : 400,
    border: `1px solid ${primary ? T.borderAccent : T.borderSecondary}`,
    background: primary ? T.bgAccentLight : "transparent",
    color: primary ? T.borderAccent : T.textWeak, opacity: disabled ? .45 : 1,
  });

  return (
    <div onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 220,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: T.bgPanel, border: `1px solid ${T.borderPrimary}`, borderRadius: 10,
          width: "100%", maxWidth: 900, maxHeight: "88vh", display: "flex", flexDirection: "column",
          fontFamily: FONT, overflow: "hidden" }}>

        <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${T.borderPrimary}` }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: T.textPrimary }}>Import BuildingConnected leveling</div>
          <div style={{ fontSize: 11, color: T.textWeak, marginTop: 5, lineHeight: 1.6 }}>
            Export a package from BC Pro's Bid Leveling tab as XLSX — the <strong style={{ color: T.textMuted }}>leveled</strong> version,
            so your adjustments and notes come through. Nothing is written until you review what landed.
          </div>
        </div>

        <div style={{ padding: "16px 24px", overflowY: "auto", flex: 1 }}>
          {!sheet && (
            <>
              <button onClick={() => fileRef.current?.click()} disabled={busy}
                style={{ ...btn(true, busy), width: "100%", padding: "22px 16px", borderStyle: "dashed" }}>
                {busy ? "Reading…" : "Choose a leveling export (.xlsx)"}
              </button>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" style={{ display: "none" }}
                onChange={e => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ""; }} />
            </>
          )}

          {error && (
            <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 3, fontSize: 11,
              background: `${T.negative}14`, border: `1px solid ${T.negative}44`, color: T.negative }}>
              {error}
            </div>
          )}

          {sheet && review && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ ...label, padding: "0 0 6px" }}>Package</div>
                  <select value={scopeId} onChange={e => setScopeId(e.target.value)} style={sel}>
                    <option value={NEW}>
                      + Create "{review.packageName || fileName.replace(/\.[^.]+$/, "")}"
                    </option>
                    {scopes.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div style={{ fontSize: 10, color: T.textWeak, alignSelf: "flex-end", paddingBottom: 6, textAlign: "right" }}>
                  <div>
                    {review.lineCount} priced line{review.lineCount === 1 ? "" : "s"} ·{" "}
                    {review.adjustmentCount} inclusion{review.adjustmentCount === 1 ? "" : "s"}
                  </div>
                  {scopeId === NEW && (
                    codeMatch
                      ? <div style={{ color: T.positive, marginTop: 2 }}>
                          Budget: {fmt(scopeBudget({ subIds: [codeMatch.id] }, divisions, itemState))} from your estimate
                        </div>
                      : <div style={{ color: T.costStatus["Needs Review"].badgeText, marginTop: 2 }}>
                          No cost code match — you will need to pick what it covers
                        </div>
                  )}
                </div>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 14 }}>
                <thead>
                  <tr>
                    <th style={{ ...label, width: "26%" }}>Bidder</th>
                    <th style={{ ...label, width: "24%" }}>Trade partner</th>
                    <th style={{ ...label, textAlign: "right" }}>BC printed</th>
                    <th style={{ ...label, textAlign: "right" }}>Their price</th>
                    <th style={{ ...label, textAlign: "right" }}>Your plugs</th>
                    <th style={{ ...label, textAlign: "right" }}>Leveled</th>
                  </tr>
                </thead>
                <tbody>
                  {review.bidders.map((b, i) => (
                    <tr key={i} style={{ borderTop: `1px solid ${T.borderPrimary}` }}>
                      <td style={{ padding: "8px", fontSize: 12, color: T.textSecondary }}>{b.bidder}</td>
                      <td style={{ padding: "6px 8px" }}>
                        <select value={choice[i] ?? NEW}
                          onChange={e => setChoice(c => c.map((x, j) => (j === i ? e.target.value : x)))}
                          style={{ ...sel, borderColor: (choice[i] ?? NEW) === NEW ? `${T.borderAccent}66` : T.borderSecondary }}>
                          <option value={NEW}>+ Add as new</option>
                          {partners.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: "8px", textAlign: "right", fontSize: 11, color: T.textWeak,
                        fontVariantNumeric: "tabular-nums" }}>
                        {b.stated == null ? "—" : fmt(b.stated)}
                      </td>
                      <td style={{ padding: "8px", textAlign: "right", fontSize: 12, color: T.textMuted,
                        fontVariantNumeric: "tabular-nums" }}>{fmt(b.own)}</td>
                      <td style={{ padding: "8px", textAlign: "right", fontSize: 12,
                        fontVariantNumeric: "tabular-nums",
                        color: b.plugged > 0 ? T.costStatus["Needs Review"].badgeText : T.textWeak }}>
                        {b.plugged > 0 ? fmt(b.plugged) : "—"}
                      </td>
                      <td style={{ padding: "8px", textAlign: "right", fontSize: 13, fontWeight: 700,
                        color: T.textSecondary, fontVariantNumeric: "tabular-nums" }}>{fmt(b.leveled)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* The whole reason this screen exists. BC prints a figure that
                  is not the leveled one, and the difference is the estimator's
                  own plugs -- which is exactly what gets forgotten between
                  exporting the sheet and reading it a week later. */}
              <div style={{ fontSize: 10, color: T.textWeak, lineHeight: 1.6, marginBottom: 14 }}>
                <strong style={{ color: T.textMuted }}>Their price</strong> is what the bidder quoted;{" "}
                <strong style={{ color: T.textMuted }}>your plugs</strong> are the figures you supplied on their
                behalf. Added together they are what each bidder is being compared at — which is not what
                BuildingConnected prints at the top of the sheet.
              </div>

              {review.skippedSections.length > 0 && (
                <div style={{ fontSize: 10, color: T.textWeak, marginBottom: 10 }}>
                  Not imported: {review.skippedSections.join(", ")}. Those carry money and are left for you to
                  bring in by hand rather than guessed at.
                </div>
              )}

              {review.warnings.length > 0 && (
                <div style={{ padding: "10px 14px", borderRadius: 3, marginBottom: 4,
                  background: `${T.costStatus["Needs Review"].badgeText}12`,
                  border: `1px solid ${T.costStatus["Needs Review"].badgeText}44` }}>
                  {review.warnings.map((w, i) => (
                    <div key={i} style={{ fontSize: 11, color: T.costStatus["Needs Review"].badgeText,
                      lineHeight: 1.6, marginBottom: i === review.warnings.length - 1 ? 0 : 6 }}>{w}</div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "14px 24px 18px", borderTop: `1px solid ${T.borderPrimary}`,
          display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, fontSize: 10, color: T.textWeak, overflow: "hidden",
            textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileName}</div>
          {sheet && <button onClick={() => { setSheet(null); setFileName(""); }} style={btn(false)}>Choose another</button>}
          <button onClick={onClose} style={btn(false)}>Cancel</button>
          <button onClick={apply} disabled={!sheet} style={btn(true, !sheet)}>
            Import {sheet ? `${review?.bidders.length} bidder${review?.bidders.length === 1 ? "" : "s"}` : ""}
          </button>
        </div>
      </div>
    </div>
  );
}
